import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  structuredData?: object;
  noindex?: boolean;
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = '/images/logo-neuropeques-optimized.svg',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  structuredData,
  noindex = false
}) => {
  const fullTitle = `${title} | Neuro Peques - Centro de Especialidades para el abordaje del Neurodesarrollo Infantil`;
  const defaultKeywords = 'neurodesarrollo infantil, psicología infantil, trastornos del neurodesarrollo, TDAH, TEA, neuropsicología, Costa Rica, Heredia, evaluación neuropsicológica, estimulación cognitiva';
  const finalKeywords = keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords;

  useEffect(() => {
    // Actualizar el título de la página
    document.title = fullTitle;
    
    // Actualizar meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
    
    // Actualizar meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', finalKeywords);
    }
  }, [title, description, finalKeywords]);

  return (
    <Helmet>
      {/* Meta tags básicos */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={finalKeywords} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* Open Graph / Facebook - Optimizado para SEO */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="180" />
      <meta property="og:image:height" content="60" />
      <meta property="og:image:alt" content="Neuro Peques - Logo del Centro de Especialidades para el abordaje del Neurodesarrollo Infantil en Heredia, Costa Rica" />
      <meta property="og:url" content={canonical || window.location.href} />
      <meta property="og:site_name" content="Neuro Peques" />
      <meta property="og:locale" content="es_CR" />
      
      {/* Twitter Cards - Optimizado para SEO */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="Neuro Peques - Logo del Centro de Especialidades para el abordaje del Neurodesarrollo Infantil en Heredia, Costa Rica" />
      <meta name="twitter:site" content="@neuropequescr" />
      <meta name="twitter:creator" content="@neuropequescr" />
      
      {/* Schema.org structured data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {/* Datos estructurados de autoría */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "MSc. Mónica Vargas Oviedo",
          "jobTitle": "Neuropsicóloga Pediátrica - Directora",
          "worksFor": {
            "@type": "MedicalClinic",
            "name": "Neuro Peques",
            "url": "https://www.neuropequescr.com"
          },
          "url": "https://www.neuropequescr.com/especialistas/monica-vargas-neuropsicologa",
          "description": "Directora y fundadora de Neuro Peques, especialista en evaluación y tratamiento de trastornos del neurodesarrollo infantil",
          "knowsAbout": [
            "Neuropsicología Infantil",
            "Trastornos del Neurodesarrollo",
            "TDAH",
            "TEA",
            "Evaluación Neuropsicológica",
            "Intervención Temprana"
          ],
          "hasCredential": [
            "Maestría en Neuropsicología Pediátrica",
            "Universidad Complutense de Madrid"
          ],
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Heredia",
            "addressCountry": "CR"
          }
        })}
      </script>
      
      {/* Meta tags adicionales para SEO */}
      <meta name="author" content="MSc. Mónica Vargas Oviedo - Neuro Peques" />
      <meta name="editor" content="MSc. Mónica Vargas Oviedo" />
      <meta name="creator" content="MSc. Mónica Vargas Oviedo" />
      <meta name="publisher" content="Neuro Peques" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      
      {/* Metadatos de autoría y edición */}
      <meta property="article:author" content="MSc. Mónica Vargas Oviedo" />
      <meta property="article:publisher" content="Neuro Peques" />
      <meta property="article:section" content="Salud Infantil" />
      <meta property="article:tag" content="neurodesarrollo infantil, neuropsicología, TDAH, TEA" />
      
      {/* Preconnect y preload para rendimiento de fuentes */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="prefetch" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Montserrat:wght@400;500;600;700;800&display=swap" as="style" />
      <link rel="preload" href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Montserrat:wght@400;500;600;700;800&display=swap" as="style" onLoad={(e) => { const target = e.target as HTMLLinkElement; target.onload = null; target.rel = 'stylesheet'; }} />
    </Helmet>
  );
};
